import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { auth as firebaseAuth, googleProvider } from "../firebase";
import { apiAuth } from "../utils/http";
import GoogleIcon from "../components/GoogleIcon";
import logo from "../assets/logo.svg";

export default function Login() {
  const { status, login, loginWithGoogle } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(searchParams.get("reason") || "");

  if (status === "ready") return <Navigate to="/" replace />;

  const signedIn = () => {
    showToast("Signed in successfully");
    navigate("/", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      showToast("Please enter your staff email or username", "info");
      return;
    }
    if (!password) {
      showToast("Please enter your password", "info");
      return;
    }
    setAlert("");
    setBusy(true);
    try {
      await login(username.trim(), password, remember);
      signedIn();
    } catch (err) {
      setAlert(err.message);
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setAlert("");
    setBusy(true);
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken();
      await loginWithGoogle(idToken, remember);
      signedIn();
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        setAlert(err.status ? err.message : "Google sign-in failed, please try again");
      }
      setBusy(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      showToast("Enter your username above, then click Forgot Password", "info");
      return;
    }
    try {
      const res = await apiAuth.post("forgot-password", { username: username.trim() });
      showToast(res.data.message || "Reset password link sent to your registered email address");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  return (
    <div className="login-backdrop active">
      <div className="login-card-container">
        <div className="login-header">
          <div className="login-brand-logo">
            <img src={logo} alt="DOTIZO ADVERTISING.LLC" className="login-logo-img" />
          </div>
          <h2 className="login-title">Job Register Portal</h2>
          <p className="login-subtitle">Sign in to access order management &amp; job register</p>
        </div>

        <div className="login-body">
          {alert && <div className="login-alert" role="alert">{alert}</div>}

          <form id="login-form" autoComplete="on" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="login-email" className="form-label">Staff Email / Username</label>
              <div className="input-with-icon">
                <svg viewBox="0 0 24 24" className="input-icon"><path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                <input
                  type="text"
                  id="login-email"
                  className="form-control"
                  placeholder="you@company.com or username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="login-password" className="form-label">Password</label>
              <div className="input-with-icon">
                <svg viewBox="0 0 24 24" className="input-icon"><path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" /></svg>
                <input
                  type={showPassword ? "text" : "password"}
                  id="login-password"
                  className="form-control"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="btn-toggle-pass"
                  title="Show/Hide Password"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                </button>
              </div>
            </div>

            <div className="login-options-row">
              <label className="remember-checkbox-label">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                <span>Remember session</span>
              </label>
              <a href="#forgot-password" className="forgot-pass-link" onClick={handleForgotPassword}>
                Forgot Password?
              </a>
            </div>

            <button type="submit" className="btn btn-primary btn-login-submit" disabled={busy}>
              <span>{busy ? "Signing In..." : "Sign In to Portal"}</span>
              <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" /></svg>
            </button>
          </form>

          <div className="login-divider">OR</div>

          <button type="button" className="btn btn-google-login" onClick={handleGoogle} disabled={busy}>
            <GoogleIcon />
            <span>Sign in with Google</span>
          </button>
        </div>

        <div className="login-footer">
          <span className="security-badge">🔒 256-Bit SSL Encrypted Portal</span>
          <p>© {new Date().getFullYear()} Job Register Portal</p>
        </div>
      </div>
    </div>
  );
}
